/**
 * AGENT TOUT TERRAIN V4 — Dashboard branché Firebase
 * ────────────────────────────────────────────────────
 * Lit les opportunités en temps réel depuis Firestore via le Worker API
 * Worker URL : https://agent-tout-terrain.<ton-subdomain>.workers.dev
 *
 * DÉPLOIEMENT :
 * 1. Copier ce fichier dans ton projet React (src/AgentV4Firebase.jsx)
 * 2. Modifier WORKER_URL avec ton URL réelle
 * 3. npm install firebase
 * 4. Déployer sur Cloudflare Pages
 */

import { useState, useEffect, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore, collection, onSnapshot, doc,
  updateDoc, addDoc, query, orderBy, serverTimestamp,
} from "firebase/firestore";

// ─── FIREBASE CONFIG ──────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyAq-TdRbKhWss3fqO1rj7nOrVP60ivKIE8",
  authDomain: "agent-tout-terrain.firebaseapp.com",
  projectId: "agent-tout-terrain",
  storageBucket: "agent-tout-terrain.firebasestorage.app",
  messagingSenderId: "143560362456",
  appId: "1:143560362456:web:4503381dc96da746460aad",
};

const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);

// ─── WORKER URL ───────────────────────────────────────────────────────────────
// Remplace par ton URL réelle après déploiement wrangler
const WORKER_URL = "https://agent-tout-terrain.ton-soihibaliousseni.workers.dev";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  bg:"#04050e", surface:"#080a17", panel:"#0c0e1f", card:"#101326",
  border:"#161a32", borderHi:"#222848",
  teal:"#00f0d0", tealDim:"#00f0d010", tealMid:"#00f0d035",
  gold:"#f5c842", goldDim:"#f5c84212",
  purple:"#9b6cf7", purpleDim:"#9b6cf710",
  red:"#f04560", redDim:"#f0456010",
  green:"#1de98a", greenDim:"#1de98a10",
  blue:"#3da8f8", blueDim:"#3da8f810",
  amber:"#f5a020", amberDim:"#f5a02010",
  txt:"#dde3fa", txtMid:"#6870a0", txtDim:"#282e52",
};

const SC = { Nouveau:C.purple,Qualifié:C.teal,Proposé:C.amber,Négociation:C.blue,Gagné:C.green,Perdu:C.red };
const STAGES = ["Nouveau","Qualifié","Proposé","Négociation","Gagné","Perdu"];
const PLATFORM_ICONS = { "Google Maps":"📍",LinkedIn:"💼",Facebook:"📘",Malt:"🟠",Upwork:"🔵",Manuel:"✏️" };

const PRODUCTS = [
  { id:"prospia",      icon:"🎯", name:"ProspIA",          price:"39–149€/mois" },
  { id:"contenuia",   icon:"🎬", name:"ContenuIA",         price:"29–99€/mois"  },
  { id:"storyverse",  icon:"📚", name:"StoryVerse",        price:"9–49€/mois"   },
  { id:"localtranslation",icon:"🌍",name:"Local Translation",price:"Freemium"   },
  { id:"siteweb",     icon:"🌐", name:"Site Web",          price:"500–3000€"    },
  { id:"automatisation",icon:"⚙️",name:"Automatisation IA",price:"200–1500€"   },
  { id:"appdev",      icon:"📱", name:"App Mobile",        price:"2000–10000€"  },
  { id:"integratioia",icon:"🤖", name:"Intégration IA",   price:"500–5000€"    },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function timeAgo(d) {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d?.toDate?.() || new Date(d);
  const s = Math.floor((Date.now() - date) / 1000);
  if (s < 60) return "À l'instant";
  if (s < 3600) return `${Math.floor(s/60)}min`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}j`;
}
function fmtMoney(n) { return (n||0).toLocaleString("fr-FR") + " €"; }
function Chip({ label, color=C.teal, small }) {
  return <span style={{ background:color+"18", color, border:`1px solid ${color}44`, borderRadius:6, padding:small?"1px 7px":"3px 10px", fontSize:small?9:11, fontWeight:700, whiteSpace:"nowrap" }}>{label}</span>;
}
function Bar({ value, max=100, color=C.teal, h=4 }) {
  return <div style={{ height:h, background:C.border, borderRadius:99, overflow:"hidden" }}><div style={{ height:"100%", width:`${Math.min(100,(value/max)*100)}%`, background:color, borderRadius:99, transition:"width .5s" }}/></div>;
}
function Ring({ value, size=46 }) {
  const pct = (value||0)/100;
  const color = pct>=.7?C.green:pct>=.45?C.amber:C.red;
  const r = size/2-4; const c = 2*Math.PI*r;
  return <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
    <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.border} strokeWidth="3"/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${pct*c} ${c}`} strokeLinecap="round"/>
    </svg>
    <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size>40?11:9, fontWeight:900, color }}>{value||0}</div>
  </div>;
}
function Kpi({ icon, label, value, color=C.teal, sub }) {
  return <div style={{ background:C.surface, border:`1px solid ${color}22`, borderRadius:10, padding:"14px 16px", minWidth:130, flex:1 }}>
    <div style={{ fontSize:22, marginBottom:4 }}>{icon}</div>
    <div style={{ fontSize:21, fontWeight:900, color, lineHeight:1 }}>{value}</div>
    <div style={{ fontSize:10, color:C.txtMid, marginTop:3 }}>{label}</div>
    {sub && <div style={{ fontSize:9, color:color+"88", marginTop:2 }}>{sub}</div>}
  </div>;
}

// ─── FORM AJOUT MANUEL ────────────────────────────────────────────────────────
function AddOppModal({ onClose }) {
  const [form, setForm] = useState({
    company:"", contact:"", contactRole:"Gérant", contactEmail:"", contactPhone:"",
    title:"", sector:"", location:"", region:"France", budget:1000,
    bestProduct:"siteweb", platform:"Manuel", notes:"",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await addDoc(collection(db, "opportunities"), {
        ...form,
        budget: Number(form.budget),
        score: 60, urgency: 5, conversion: 50, difficulty: 3, competition: 3,
        status: "Nouveau", commercial: "ali", module: "Manuel",
        bestChannel: "Email", strategy: "À définir",
        keywords: [], history: [], tasks: [], devis: [], contrats: [], factures: [], rdv: [],
        hasWebsite: false, auditScore: null, source: "Manuel",
        detectedAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      onClose();
    } catch (e) { console.error(e); }
    setSaving(false);
  }

  const fields = [
    ["Entreprise *","company","text"], ["Contact *","contact","text"],
    ["Rôle","contactRole","text"], ["Email","contactEmail","email"],
    ["Téléphone","contactPhone","text"], ["Titre de la mission *","title","text"],
    ["Secteur","sector","text"], ["Ville / Région","location","text"],
    ["Budget estimé (€)","budget","number"],
  ];

  return (
    <div style={{ position:"fixed", inset:0, background:"#000000ee", zIndex:400, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:16, width:"100%", maxWidth:560, maxHeight:"90vh", overflow:"auto" }}>
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ flex:1 }}><div style={{ fontWeight:900, fontSize:14, color:C.teal }}>✏️ Ajouter une opportunité</div><div style={{ fontSize:10, color:C.txtMid }}>Ajout manuel → sauvegardé dans Firebase</div></div>
          <button onClick={onClose} style={{ background:"none", border:`1px solid ${C.border}`, color:C.txtMid, borderRadius:8, padding:"4px 12px", cursor:"pointer" }}>✕</button>
        </div>
        <div style={{ padding:"18px 20px", display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {fields.map(([label, field, type]) => (
              <div key={field}>
                <div style={{ fontSize:9, color:C.txtDim, marginBottom:3 }}>{label.toUpperCase()}</div>
                <input type={type} value={form[field]} onChange={e => setForm(p => ({...p, [field]: e.target.value}))}
                  style={{ width:"100%", background:C.card, border:`1px solid ${C.border}`, borderRadius:7, padding:"8px 10px", color:C.txt, fontSize:12, outline:"none", boxSizing:"border-box" }}/>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize:9, color:C.txtDim, marginBottom:5 }}>PRODUIT RECOMMANDÉ</div>
            <select value={form.bestProduct} onChange={e => setForm(p => ({...p, bestProduct: e.target.value}))}
              style={{ width:"100%", background:C.card, border:`1px solid ${C.border}`, borderRadius:7, padding:"8px 10px", color:C.txt, fontSize:12, outline:"none" }}>
              {PRODUCTS.map(p => <option key={p.id} value={p.id}>{p.icon} {p.name} — {p.price}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:9, color:C.txtDim, marginBottom:3 }}>NOTES</div>
            <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={3}
              style={{ width:"100%", background:C.card, border:`1px solid ${C.border}`, borderRadius:7, padding:"8px 10px", color:C.txt, fontSize:12, outline:"none", resize:"vertical", boxSizing:"border-box" }}/>
          </div>
          <button onClick={save} disabled={saving || !form.company || !form.title}
            style={{ background:saving||!form.company||!form.title?C.border:`linear-gradient(135deg,${C.teal},${C.purple})`, border:"none", borderRadius:10, padding:"12px", color:saving?"#888":"#04050e", cursor:saving?"not-allowed":"pointer", fontWeight:900, fontSize:13 }}>
            {saving ? "Sauvegarde..." : "💾 Enregistrer dans Firebase"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── OPP CARD ─────────────────────────────────────────────────────────────────
function OppCard({ opp, selected, onSelect }) {
  const product = PRODUCTS.find(p => p.id === opp.bestProduct);
  const isSel = selected?.id === opp.id;
  const hist = typeof opp.history === "string" ? JSON.parse(opp.history || "[]") : (opp.history || []);
  const devis = typeof opp.devis === "string" ? JSON.parse(opp.devis || "[]") : (opp.devis || []);
  const contrats = typeof opp.contrats === "string" ? JSON.parse(opp.contrats || "[]") : (opp.contrats || []);

  return (
    <div onClick={() => onSelect(opp)} style={{ background:isSel?C.panel:C.surface, border:`1px solid ${isSel?C.teal:C.border}`, borderLeft:`3px solid ${SC[opp.status]||C.purple}`, borderRadius:10, padding:"12px 14px", cursor:"pointer" }}>
      <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
        <Ring value={opp.score||50}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", gap:5, alignItems:"center", flexWrap:"wrap", marginBottom:4 }}>
            <Chip label={opp.status||"Nouveau"} color={SC[opp.status]||C.purple} small/>
            {(opp.urgency||0)>=8 && <Chip label="🔥" color={C.red} small/>}
            {devis.length>0 && <Chip label="📄" color={C.gold} small/>}
            {contrats.length>0 && <Chip label="✍️" color={C.green} small/>}
            <span style={{ marginLeft:"auto", fontSize:9, color:C.txtDim }}>{timeAgo(opp.detectedAt)}</span>
          </div>
          <div style={{ fontWeight:700, fontSize:13, color:C.txt, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:2 }}>{opp.title}</div>
          <div style={{ fontSize:11, color:C.txtMid }}>{opp.company} · {opp.contact||"À contacter"} · 📍{opp.location||opp.region}</div>
          <div style={{ display:"flex", gap:5, marginTop:7, flexWrap:"wrap" }}>
            {product && <Chip label={`${product.icon} ${product.name}`} color={C.purple} small/>}
            <Chip label={fmtMoney(opp.budget||0)} color={C.green} small/>
            <Chip label={`Conv. ${opp.conversion||50}%`} color={C.teal} small/>
            <Chip label={(PLATFORM_ICONS[opp.platform]||"🔍")+" "+(opp.platform||"")} color={C.txtMid} small/>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DETAIL PANEL ─────────────────────────────────────────────────────────────
function DetailPanel({ opp, onClose, onStatusChange }) {
  const [tab, setTab] = useState("analyse");
  const [notes, setNotes] = useState(opp.notes || "");
  const [saving, setSaving] = useState(false);
  const product = PRODUCTS.find(p => p.id === opp.bestProduct);
  const history = typeof opp.history === "string" ? JSON.parse(opp.history||"[]") : (opp.history||[]);

  async function saveNotes() {
    if (!opp.id) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "opportunities", opp.id), { notes, updatedAt: serverTimestamp() });
    } catch(e) { console.error(e); }
    setSaving(false);
  }

  async function changeStatus(status) {
    if (!opp.id) return;
    try {
      await updateDoc(doc(db, "opportunities", opp.id), {
        status, updatedAt: serverTimestamp(),
        history: JSON.stringify([...history, { date:new Date().toISOString(), action:`Statut → ${status}`, icon:"🔄" }]),
      });
      onStatusChange(opp.id, status);
    } catch(e) { console.error(e); }
  }

  return (
    <div style={{ width:360, flexShrink:0, background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, display:"flex", flexDirection:"column", maxHeight:"80vh", overflow:"hidden", position:"sticky", top:0 }}>
      <div style={{ padding:"13px 15px", borderBottom:`1px solid ${C.border}`, background:C.card }}>
        <div style={{ display:"flex", justifyContent:"space-between" }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:900, fontSize:13, color:C.txt }}>{opp.company}</div>
            <div style={{ fontSize:11, color:C.txtMid, marginTop:1 }}>{opp.contact} · {opp.contactRole}</div>
            {opp.contactEmail && <div style={{ fontSize:10, color:C.teal, marginTop:1 }}>{opp.contactEmail}</div>}
            {opp.contactPhone && <div style={{ fontSize:10, color:C.blue, marginTop:1 }}>📞 {opp.contactPhone}</div>}
            {opp.website && <div style={{ fontSize:10, color:opp.hasWebsite?C.green:C.red, marginTop:1 }}>🌐 {opp.hasWebsite?"Site présent":"Sans site web"} {opp.website&&`— ${opp.website}`}</div>}
            {opp.rating && <div style={{ fontSize:10, color:C.amber, marginTop:1 }}>⭐ {opp.rating}/5 ({opp.reviewCount||0} avis Google)</div>}
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.txtDim, cursor:"pointer", fontSize:16 }}>✕</button>
        </div>
      </div>

      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}` }}>
        {["analyse","timeline","crm"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ flex:1, padding:"7px 2px", background:"none", border:"none", cursor:"pointer", fontSize:10, fontWeight:700, textTransform:"capitalize", color:tab===t?C.teal:C.txtDim, borderBottom:`2px solid ${tab===t?C.teal:"transparent"}` }}>{t}</button>
        ))}
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"13px 15px" }}>
        {tab==="analyse" && (
          <div style={{ display:"flex", flexDirection:"column", gap:11 }}>
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <Ring value={opp.score||50} size={52}/>
              <div style={{ flex:1 }}>
                {[{l:"Urgence",v:opp.urgency||5,max:10},{l:"Conversion",v:opp.conversion||50,max:100,sfx:"%"},{l:"Difficulté",v:opp.difficulty||5,max:10}].map(({l,v,max,sfx=""}) => (
                  <div key={l} style={{ marginBottom:6 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:C.txtMid, marginBottom:2 }}><span>{l}</span><span style={{ color:C.txt }}>{v}{sfx}</span></div>
                    <Bar value={v} max={max} color={v/max>.7?C.green:v/max>.4?C.amber:C.red}/>
                  </div>
                ))}
              </div>
            </div>
            {product && (
              <div style={{ background:C.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:9, color:C.txtDim, marginBottom:5 }}>RECOMMANDATION AGENT</div>
                <div style={{ fontSize:13, color:C.teal, fontWeight:700 }}>{product.icon} {product.name} — {product.price}</div>
                <div style={{ fontSize:10, color:C.txtMid, marginTop:2 }}>Canal : {opp.bestChannel}</div>
                <div style={{ fontSize:10, color:C.green, marginTop:1 }}>Budget : {fmtMoney(opp.budget)}</div>
              </div>
            )}
            {opp.strategy && (
              <div style={{ background:C.card, borderRadius:8, padding:"10px 12px", border:`1px solid ${C.purple}33` }}>
                <div style={{ fontSize:9, color:C.txtDim, marginBottom:4 }}>STRATÉGIE</div>
                <div style={{ fontSize:11, color:C.txt, lineHeight:1.6 }}>{opp.strategy}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize:9, color:C.txtDim, marginBottom:5 }}>PIPELINE</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                {STAGES.map(s => (
                  <button key={s} onClick={() => changeStatus(s)} style={{ background:opp.status===s?SC[s]+"33":"none", border:`1px solid ${opp.status===s?SC[s]:C.border}`, color:opp.status===s?SC[s]:C.txtDim, borderRadius:6, padding:"2px 8px", fontSize:9, cursor:"pointer" }}>{s}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab==="timeline" && (
          <div>
            <div style={{ fontSize:9, color:C.txtDim, marginBottom:8 }}>HISTORIQUE FIREBASE</div>
            {history.length===0 && <div style={{ fontSize:11, color:C.txtDim }}>Aucune action</div>}
            {history.map((h,i) => (
              <div key={i} style={{ display:"flex", gap:8, paddingBottom:7, marginBottom:7, borderBottom:i<history.length-1?`1px solid ${C.border}`:"none" }}>
                <div style={{ fontSize:14, flexShrink:0 }}>{h.icon||"📌"}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11, color:C.txt }}>{h.action}</div>
                  <div style={{ fontSize:9, color:C.txtDim, marginTop:1 }}>{h.date ? new Date(h.date).toLocaleDateString("fr-FR") : ""}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab==="crm" && (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div>
              <div style={{ fontSize:9, color:C.txtDim, marginBottom:5 }}>NOTES (synchronisées Firebase)</div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                style={{ width:"100%", background:C.card, border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 10px", color:C.txt, fontSize:11, resize:"vertical", minHeight:80, outline:"none", boxSizing:"border-box" }}/>
              <button onClick={saveNotes} disabled={saving} style={{ marginTop:6, width:"100%", background:C.tealDim, border:`1px solid ${C.tealMid}`, color:C.teal, borderRadius:8, padding:"7px", cursor:"pointer", fontSize:11, fontWeight:700 }}>
                {saving ? "Sauvegarde..." : "💾 Sauvegarder dans Firebase"}
              </button>
            </div>
            {opp.keywords?.length>0 && (
              <div>
                <div style={{ fontSize:9, color:C.txtDim, marginBottom:5 }}>MOTS-CLÉS IA</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                  {(typeof opp.keywords==="string"?JSON.parse(opp.keywords||"[]"):opp.keywords).map(k => <Chip key={k} label={k} color={C.purple} small/>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function AgentV4Firebase() {
  const [opps, setOpps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("centre");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanLog, setScanLog] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [sf, setSf] = useState("Tous");
  const [sq, setSq] = useState("");
  const [lastSync, setLastSync] = useState(null);

  // ── Écoute Firestore en temps réel ──────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, "opportunities"), orderBy("detectedAt", "desc"));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setOpps(data);
      setLastSync(new Date());
      setLoading(false);
    }, (err) => {
      console.error("Firestore error:", err);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // ── Lance le Worker manuellement ─────────────────────────────────────────────
  async function triggerScan() {
    setScanning(true);
    setScanLog([{ text:"⚡ Déclenchement Worker Cloudflare...", ts:new Date() }]);
    try {
      const res = await fetch(`${WORKER_URL}/scan`);
      if (res.ok) {
        setScanLog(p => [...p, { text:"✅ Worker démarré — scan Google Maps en cours", ts:new Date() }]);
        setScanLog(p => [...p, { text:"📍 Recherche dans 11 zones francophones", ts:new Date() }]);
        setScanLog(p => [...p, { text:"🤖 Scoring Claude en cours pour chaque résultat", ts:new Date() }]);
        setScanLog(p => [...p, { text:"💾 Écriture dans Firebase Firestore", ts:new Date() }]);
        setScanLog(p => [...p, { text:"🔴 Les résultats apparaîtront ici automatiquement", ts:new Date() }]);
      } else {
        setScanLog(p => [...p, { text:`⚠️ WORKER_URL à configurer (${WORKER_URL})`, ts:new Date() }]);
      }
    } catch {
      setScanLog(p => [...p, { text:`⚠️ Worker non accessible — configure WORKER_URL`, ts:new Date() }]);
    }
    setScanning(false);
  }

  function updateStatusLocal(id, status) {
    setOpps(p => p.map(o => o.id === id ? {...o, status} : o));
    if (selected?.id === id) setSelected(p => ({...p, status}));
  }

  // ── Stats ────────────────────────────────────────────────────────────────────
  const pipeline = opps.filter(o=>o.status!=="Perdu").reduce((s,o)=>s+(o.budget||0),0);
  const gagné    = opps.filter(o=>o.status==="Gagné").reduce((s,o)=>s+(o.budget||0),0);
  const urgent   = opps.filter(o=>(o.urgency||0)>=8 && o.status==="Nouveau").length;
  const noSite   = opps.filter(o=>!o.hasWebsite).length;

  const filtered = opps.filter(o => {
    if (sf!=="Tous" && o.status!==sf) return false;
    if (sq && !o.title?.toLowerCase().includes(sq.toLowerCase()) && !o.company?.toLowerCase().includes(sq.toLowerCase())) return false;
    return true;
  });

  const pipelineCounts = STAGES.reduce((acc,s) => ({...acc, [s]:opps.filter(o=>o.status===s).length}), {});

  const TABS = [
    { id:"centre",label:"Commande",icon:"⚡" },
    { id:"opportunites",label:"Opportunités",icon:"🎯" },
    { id:"pipeline",label:"Kanban",icon:"📊" },
    { id:"stats",label:"Analytics",icon:"📈" },
  ];

  return (
    <div style={{ background:C.bg, minHeight:"100vh", color:C.txt, fontFamily:"'Inter',system-ui,sans-serif", fontSize:14 }}>
      {/* NAV */}
      <nav style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:"0 18px", display:"flex", alignItems:"center", gap:6, height:54, position:"sticky", top:0, zIndex:50 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginRight:10 }}>
          <div style={{ width:30, height:30, borderRadius:8, background:`linear-gradient(135deg,${C.teal},${C.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>🤖</div>
          <div>
            <div style={{ fontWeight:900, fontSize:12, letterSpacing:-.5 }}>Agent Tout Terrain</div>
            <div style={{ fontSize:8, color:C.teal }}>V4 · FIREBASE LIVE{lastSync?` · Sync ${timeAgo(lastSync)}`:""}</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:2, flex:1, overflowX:"auto" }}>
          {TABS.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={{ background:tab===t.id?C.tealDim:"none", border:`1px solid ${tab===t.id?C.tealMid:"transparent"}`, color:tab===t.id?C.teal:C.txtMid, borderRadius:8, padding:"5px 10px", cursor:"pointer", fontSize:10, fontWeight:700, whiteSpace:"nowrap", flexShrink:0 }}>{t.icon} {t.label}</button>)}
        </div>
        <div style={{ display:"flex", gap:5, marginLeft:"auto", flexShrink:0 }}>
          {loading && <span style={{ fontSize:10, color:C.teal }}>⟳ Chargement Firebase...</span>}
          <button onClick={() => setShowAdd(true)} style={{ background:C.goldDim, border:`1px solid ${C.gold}55`, borderRadius:8, padding:"5px 12px", color:C.gold, cursor:"pointer", fontWeight:800, fontSize:10 }}>✏️ Ajouter</button>
          <button onClick={triggerScan} disabled={scanning} style={{ background:scanning?C.panel:`linear-gradient(135deg,${C.teal},${C.purple})`, border:"none", borderRadius:8, padding:"5px 12px", color:scanning?C.txtMid:"#04050e", cursor:scanning?"not-allowed":"pointer", fontWeight:800, fontSize:10 }}>{scanning?"⟳ Scan...":"🔭 Scanner"}</button>
        </div>
      </nav>

      <div style={{ padding:"18px", maxWidth:1480, margin:"0 auto" }}>
        {/* ── CENTRE ── */}
        {tab==="centre" && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:9 }}>
              <Kpi icon="📡" label="Sync Firebase" value={opps.length} color={C.teal} sub="Temps réel"/>
              <Kpi icon="💰" label="Pipeline Total" value={fmtMoney(pipeline)} color={C.green}/>
              <Kpi icon="🏆" label="CA Gagné" value={fmtMoney(gagné)} color={C.gold}/>
              <Kpi icon="🔥" label="Urgentes" value={urgent} color={C.red}/>
              <Kpi icon="🌐" label="Sans site web" value={noSite} color={C.purple} sub="Prospect Finder"/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {/* Scan log */}
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:11, padding:14 }}>
                <div style={{ fontWeight:700, fontSize:11, color:C.txt, marginBottom:8 }}>⚡ Worker Cloudflare — Log</div>
                {scanLog.length===0
                  ? <div style={{ fontSize:10, color:C.txtDim, padding:"14px 0", textAlign:"center" }}>Lancez un scan pour déclencher le Worker</div>
                  : scanLog.map((l,i) => (
                    <div key={i} style={{ display:"flex", gap:7, fontSize:10, marginBottom:4 }}>
                      <span style={{ color:C.txtDim, minWidth:42 }}>{l.ts.toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit",second:"2-digit"})}</span>
                      <span style={{ color:C.txtMid }}>{l.text}</span>
                    </div>
                  ))
                }
              </div>
              {/* Pipeline */}
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:11, padding:14 }}>
                <div style={{ fontWeight:700, fontSize:11, color:C.txt, marginBottom:8 }}>📊 Pipeline Live Firebase</div>
                {STAGES.map(s => {
                  const rev = opps.filter(o=>o.status===s).reduce((t,o)=>t+(o.budget||0),0);
                  return (
                    <div key={s} style={{ display:"flex", alignItems:"center", gap:7, marginBottom:7 }}>
                      <div style={{ width:6, height:6, borderRadius:"50%", background:SC[s], flexShrink:0 }}/>
                      <div style={{ flex:1, fontSize:11, color:C.txtMid }}>{s}</div>
                      <span style={{ background:SC[s]+"18", color:SC[s], border:`1px solid ${SC[s]}44`, borderRadius:6, padding:"1px 7px", fontSize:9, fontWeight:700 }}>{pipelineCounts[s]||0}</span>
                      <div style={{ fontSize:9, color:C.green, minWidth:55, textAlign:"right" }}>{rev.toLocaleString()}€</div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Top 5 */}
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:11, padding:14 }}>
              <div style={{ fontWeight:700, fontSize:11, color:C.txt, marginBottom:10 }}>⭐ Meilleures opportunités Firebase</div>
              {loading
                ? <div style={{ fontSize:12, color:C.txtDim, padding:"20px 0", textAlign:"center" }}>⟳ Chargement depuis Firestore...</div>
                : opps.length===0
                ? <div style={{ fontSize:12, color:C.txtDim, padding:"20px 0", textAlign:"center" }}>
                    Aucune opportunité en base.<br/>
                    <span style={{ fontSize:10 }}>Lancez un scan ou ajoutez manuellement.</span>
                  </div>
                : [...opps].sort((a,b)=>(b.score||0)-(a.score||0)).slice(0,5).map(o => (
                  <div key={o.id} onClick={() => { setSelected(o); setTab("opportunites"); }} style={{ display:"flex", gap:10, alignItems:"center", background:C.card, border:`1px solid ${C.border}`, borderLeft:`3px solid ${SC[o.status]||C.purple}`, borderRadius:9, padding:"10px 13px", marginBottom:7, cursor:"pointer" }}>
                    <Ring value={o.score||50} size={38}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:C.txt, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.company}</div>
                      <div style={{ fontSize:10, color:C.txtMid }}>{o.location||o.region} · {o.sector}</div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontSize:14, fontWeight:900, color:C.green }}>{fmtMoney(o.budget)}</div>
                      <span style={{ background:(SC[o.status]||C.purple)+"18", color:SC[o.status]||C.purple, border:`1px solid ${(SC[o.status]||C.purple)}44`, borderRadius:6, padding:"1px 7px", fontSize:9, fontWeight:700 }}>{o.status||"Nouveau"}</span>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* ── OPPORTUNITÉS ── */}
        {tab==="opportunites" && (
          <div style={{ display:"flex", gap:13 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", gap:7, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
                <input value={sq} onChange={e => setSq(e.target.value)} placeholder="🔍 Rechercher..." style={{ background:C.surface, border:`1px solid ${C.border}`, color:C.txt, borderRadius:8, padding:"6px 11px", fontSize:11, outline:"none", flex:1, minWidth:140 }}/>
                <select value={sf} onChange={e => setSf(e.target.value)} style={{ background:C.surface, border:`1px solid ${C.border}`, color:C.txtMid, borderRadius:8, padding:"6px 9px", fontSize:10 }}>
                  <option>Tous</option>{STAGES.map(s => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => setShowAdd(true)} style={{ background:C.goldDim, border:`1px solid ${C.gold}44`, color:C.gold, borderRadius:8, padding:"6px 12px", cursor:"pointer", fontSize:10, fontWeight:700 }}>✏️ Ajouter</button>
                <span style={{ fontSize:10, color:C.txtDim }}>{filtered.length} dans Firebase</span>
              </div>
              {loading
                ? <div style={{ textAlign:"center", padding:"40px 0", color:C.txtMid, fontSize:13 }}>⟳ Connexion Firestore...</div>
                : filtered.length===0
                ? <div style={{ textAlign:"center", padding:"40px 0", color:C.txtDim, fontSize:12 }}>Aucune opportunité.<br/><span style={{ fontSize:10 }}>Lancez un scan ou ajoutez manuellement.</span></div>
                : <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                    {filtered.map(o => <OppCard key={o.id} opp={o} selected={selected} onSelect={setSelected}/>)}
                  </div>
              }
            </div>
            {selected && <DetailPanel opp={selected} onClose={() => setSelected(null)} onStatusChange={updateStatusLocal}/>}
          </div>
        )}

        {/* ── KANBAN ── */}
        {tab==="pipeline" && (
          <div>
            <div style={{ fontSize:11, color:C.txtMid, marginBottom:12 }}>Pipeline Firebase — mise à jour en temps réel</div>
            <div style={{ display:"flex", gap:9, overflowX:"auto", paddingBottom:8 }}>
              {STAGES.map(stage => (
                <div key={stage} style={{ minWidth:200, flex:"0 0 200px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:7, padding:"5px 9px", background:SC[stage]+"15", border:`1px solid ${SC[stage]}44`, borderRadius:7 }}>
                    <div style={{ width:6, height:6, borderRadius:"50%", background:SC[stage] }}/>
                    <span style={{ fontWeight:800, fontSize:10, color:SC[stage], flex:1 }}>{stage}</span>
                    <span style={{ fontSize:9, color:SC[stage]+"88" }}>{fmtMoney(opps.filter(o=>o.status===stage).reduce((s,o)=>s+(o.budget||0),0))}</span>
                  </div>
                  {opps.filter(o=>o.status===stage).map(o => (
                    <div key={o.id} onClick={() => { setSelected(o); setTab("opportunites"); }} style={{ background:C.surface, border:`1px solid ${C.border}`, borderLeft:`3px solid ${SC[stage]}`, borderRadius:8, padding:"9px 11px", marginBottom:6, cursor:"pointer" }}>
                      <div style={{ fontSize:10, fontWeight:700, color:C.txt, lineHeight:1.4, marginBottom:3 }}>{(o.title||"").length>50?(o.title||"").slice(0,48)+"…":(o.title||"")}</div>
                      <div style={{ fontSize:9, color:C.txtMid, marginBottom:4 }}>{o.company} · {o.location||o.region}</div>
                      <div style={{ fontSize:10, fontWeight:800, color:C.green }}>{fmtMoney(o.budget)}</div>
                    </div>
                  ))}
                  {opps.filter(o=>o.status===stage).length===0 && <div style={{ fontSize:10, color:C.txtDim, padding:"12px 0", textAlign:"center" }}>Vide</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ANALYTICS ── */}
        {tab==="stats" && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
              {/* Par région */}
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:11, padding:14 }}>
                <div style={{ fontWeight:700, fontSize:11, color:C.txt, marginBottom:10 }}>📍 Par Région</div>
                {[...new Set(opps.map(o=>o.region||"Inconnue"))].map(r => {
                  const cnt = opps.filter(o=>o.region===r).length;
                  return (
                    <div key={r} style={{ marginBottom:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:C.txtMid, marginBottom:2 }}><span>{r}</span><span style={{ color:C.teal }}>{cnt}</span></div>
                      <Bar value={cnt} max={opps.length||1} color={C.teal}/>
                    </div>
                  );
                })}
              </div>
              {/* Par secteur */}
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:11, padding:14 }}>
                <div style={{ fontWeight:700, fontSize:11, color:C.txt, marginBottom:10 }}>🏢 Par Secteur</div>
                {[...new Set(opps.map(o=>o.sector||"Autre"))].slice(0,8).map(s => {
                  const cnt = opps.filter(o=>o.sector===s).length;
                  return (
                    <div key={s} style={{ marginBottom:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:C.txtMid, marginBottom:2 }}><span>{s}</span><span style={{ color:C.purple }}>{cnt}</span></div>
                      <Bar value={cnt} max={opps.length||1} color={C.purple}/>
                    </div>
                  );
                })}
              </div>
              {/* Par produit */}
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:11, padding:14 }}>
                <div style={{ fontWeight:700, fontSize:11, color:C.txt, marginBottom:10 }}>🎯 Produits recommandés</div>
                {PRODUCTS.map(p => {
                  const cnt = opps.filter(o=>o.bestProduct===p.id).length;
                  if (!cnt) return null;
                  return (
                    <div key={p.id} style={{ marginBottom:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:C.txtMid, marginBottom:2 }}><span>{p.icon} {p.name}</span><span style={{ color:C.green }}>{cnt}</span></div>
                      <Bar value={cnt} max={opps.length||1} color={C.green}/>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Stats globales */}
            <div style={{ background:C.surface, border:`1px solid ${C.teal}33`, borderRadius:11, padding:14 }}>
              <div style={{ fontWeight:700, fontSize:11, color:C.teal, marginBottom:10 }}>📡 Firebase Stats</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:8 }}>
                {[
                  { l:"Total opportunités", v:opps.length, c:C.teal },
                  { l:"Score moyen IA", v:`${Math.round(opps.reduce((s,o)=>s+(o.score||0),0)/(opps.length||1))}/100`, c:C.amber },
                  { l:"Conv. moyenne", v:`${Math.round(opps.reduce((s,o)=>s+(o.conversion||0),0)/(opps.length||1))}%`, c:C.green },
                  { l:"CA potentiel total", v:fmtMoney(pipeline), c:C.gold },
                  { l:"Sans site web", v:noSite, c:C.red },
                  { l:"Zones couvertes", v:"11", c:C.purple },
                ].map(k => (
                  <div key={k.l} style={{ background:C.card, border:`1px solid ${k.c}22`, borderRadius:8, padding:"10px 12px" }}>
                    <div style={{ fontSize:18, fontWeight:900, color:k.c }}>{k.v}</div>
                    <div style={{ fontSize:9, color:C.txtMid, marginTop:2 }}>{k.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {showAdd && <AddOppModal onClose={() => setShowAdd(false)}/>}
    </div>
  );
}
